import { useState } from 'react';
import {
  Trash2, ListFilter, Settings2, Asterisk,
} from 'lucide-react';
import { FieldInput } from './field-input';
import { QuickFormatSelect } from './quick-format-select';
import type { EventFieldPath, PicklistMapping, FieldFormatConfig } from '@/types/field-mapping-flow';

interface PickListValue {
  value: string;
  label: string;
}

interface FieldRowProps {
  fieldApiName: string;
  fieldDisplayLabel: string;
  dataType: string;
  required: boolean;
  mappedValue: string;
  quickFormat: string;
  hasPicklist: boolean;
  pickListValues: PickListValue[];
  picklistMapping: PicklistMapping[];
  formatConfig: Record<string, FieldFormatConfig>;
  availableEventFields: EventFieldPath[];
  onValueChange: (value: string) => void;
  onQuickFormatChange: (format: string) => void;
  onPicklistClick: () => void;
  onAdvancedClick: () => void;
  onRemove: () => void;
  /** When true, hides settings bar (quick format, picklist, advanced) — for single-module apps */
  simpleMode?: boolean;
  /** Short explanation shown under the input (e.g. why a value was prefilled) */
  hint?: string;
}

export function FieldRow({
  fieldApiName,
  fieldDisplayLabel,
  dataType,
  required,
  mappedValue,
  quickFormat,
  hasPicklist,
  pickListValues,
  picklistMapping,
  formatConfig,
  availableEventFields,
  onValueChange,
  onQuickFormatChange,
  onPicklistClick,
  onAdvancedClick,
  onRemove,
  simpleMode = false,
  hint,
}: FieldRowProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasAdvancedFormat = Object.keys(formatConfig).length > 0;
  const hasPicklistMapping = picklistMapping.length > 0;
  const hasNonDefaultFormat = quickFormat !== 'parse_text';
  const hasAnySettings = hasAdvancedFormat || hasPicklistMapping || hasNonDefaultFormat;

  return (
    <div className="group relative">
      {/* ─── Label row ─── */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1 min-w-0">
          {required && <Asterisk className="h-2.5 w-2.5 shrink-0 text-destructive/70" />}
          <span className="truncate text-[11px] font-medium text-foreground" title={fieldDisplayLabel}>
            {fieldDisplayLabel}
          </span>
          <span className="text-[9px] text-muted-foreground/40">&middot;</span>
          <span className="truncate text-[9px] text-muted-foreground/40 font-mono" title={fieldApiName}>
            {fieldApiName}
          </span>
        </div>
        <div className="flex items-center shrink-0">
          {/* Settings toggle — hidden in simple mode */}
          {!simpleMode && (
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={`relative flex h-5 w-5 items-center justify-center rounded transition-all duration-150 ${
                settingsOpen || hasAnySettings
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'text-muted-foreground/40 hover:text-muted-foreground'
              }`}
              title="Field settings"
            >
              <Settings2 className="h-3 w-3 opacity-90 cursor-pointer" />
              {hasAnySettings && (
                <span className="absolute -right-px -top-px h-1 w-1 rounded-full bg-primary" />
              )}
            </button>
          )}
          {/* Delete — slides in from right on hover, pushes settings left */}
          {!required && (
            <button
              onClick={onRemove}
              className="flex h-5 items-center justify-center rounded transition-all duration-150 w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 overflow-hidden text-muted-foreground/30 hover:!text-destructive"
              title="Remove field"
            >
              <Trash2 className="h-3 w-3 shrink-0 cursor-pointer" />
            </button>
          )}
        </div>
      </div>

      {/* ─── Input — single input with picklist dropdown on focus + # for event fields ─── */}
      <FieldInput
        value={mappedValue}
        onChange={onValueChange}
        availableFields={availableEventFields}
        pickListValues={pickListValues}
        placeholder={hasPicklist ? 'Select or type value, # for fields...' : 'Type value or use # for event fields...'}
        className="py-1.5 text-[11px]"
      />

      {/* ─── Hint — why this value is here (e.g. the prefilled dummy email) ─── */}
      {hint && (
        <p className="mt-1 text-[10px] leading-snug text-muted-foreground">{hint}</p>
      )}

      {/* ─── Settings bar (collapsible, slim) — hidden in simple mode ─── */}
      {!simpleMode && (settingsOpen || hasAnySettings) && (
        <div className={`mt-1 flex items-center gap-1.5 animate-fade-in ${!mappedValue ? 'opacity-30 pointer-events-none' : ''}`}>
          <QuickFormatSelect
            value={quickFormat}
            onChange={onQuickFormatChange}
            onAdvancedClick={onAdvancedClick}
          />

          {hasPicklist && (
            <button
              onClick={onPicklistClick}
              className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] transition-colors ${
                hasPicklistMapping
                  ? 'bg-primary/8 text-primary font-medium'
                  : 'text-muted-foreground/50 hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <ListFilter className="h-3 w-3" />
              Picklist
            </button>
          )}

          <button
            onClick={onAdvancedClick}
            className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] transition-colors ${
              hasAdvancedFormat
                ? 'bg-primary/8 text-primary font-medium'
                : 'text-muted-foreground/50 hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Settings2 className="h-3 w-3" />
            Advanced{hasAdvancedFormat ? ` (${Object.keys(formatConfig).length})` : ''}
          </button>
        </div>
      )}
    </div>
  );
}
